import { serviceClient } from '@yola/ws-sdk';
import createPhotoItem from '../helpers/create-photo-item';
import { GALLERY_ITEMS_BATCH_SIZE } from '../constants/common';

const DEFAULT_PHRASE = '';

function getDefaultPhrase() {
  // "Search requests without a phrase parameter are not supported and may not always work." - from Getty Images API documentation.
  // We use an empty phrase parameter as a fallback when no query is provided, and haven't encountered any issues with this approach.
  // If problems arise with empty phrase searches, we should reconsider this decision and determine what default phrase to use and where to source it from.
  // Link to the Getty Images API documentation: https://api.gettyimages.com/swagger/index.html#/Search/get_v3_search_images_creative

  return DEFAULT_PHRASE;
}

function createIstockApiAdapter() {
  const { gettyImagesSearch } = serviceClient.get();
  const defaultPhrase = getDefaultPhrase();

  const normalizeData = (data) => {
    if (Array.isArray(data)) {
      return data;
    }

    if (data && data.images && Array.isArray(data.images)) {
      return data.images;
    }

    return [];
  };

  const getComputedData = (data) => {
    const normalizedData = normalizeData(data);
    return normalizedData
      .map((item) => {
        const { id, displaySizes, maxDimensions, caption } = item;

        const previewSize = displaySizes?.find((size) => size.name === 'preview');
        const imageUrl = previewSize?.uri || displaySizes?.[0]?.uri || '';

        if (!imageUrl) {
          return null;
        }

        return createPhotoItem({
          id,
          src: imageUrl,
          width: maxDimensions?.width || 0,
          height: maxDimensions?.height || 0,
          alt: caption,
        });
      })
      .filter(Boolean);
  };

  const getInitialPhotos = (params = {}) => {
    const { query, abortController } = params;

    return gettyImagesSearch(
      {
        page_size: GALLERY_ITEMS_BATCH_SIZE,
        phrase: query || defaultPhrase,
      },
      { ...(abortController && { abortController }) }
    );
  };

  const searchPhotos = (params = {}) => {
    const { query, pageSize = GALLERY_ITEMS_BATCH_SIZE, abortController } = params;

    return gettyImagesSearch(
      {
        page_size: pageSize,
        phrase: query || defaultPhrase,
      },
      { ...(abortController && { abortController }) }
    );
  };

  const getPhotos = (params = {}) => {
    const { pageSize = GALLERY_ITEMS_BATCH_SIZE, abortController } = params;

    return gettyImagesSearch(
      {
        page_size: pageSize,
        phrase: defaultPhrase,
      },
      { ...(abortController && { abortController }) }
    );
  };

  return {
    getInitialPhotos,
    searchPhotos,
    getPhotos,
    getComputedData,
  };
}

export default createIstockApiAdapter;
